// components/artifacts/charts/LineChartComponent.tsx
import React from "react";
import { LineChart, Line, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { GraphData } from "@/types/Artifact";
import { formatNumber } from "@/lib/utils";

interface LineChartComponentProps {
  data: GraphData;
  config: Record<string, any>;
  visualizationKeys: string[];
  colors: string[];
  id?: string;
}

// Custom Y-axis tick formatter that includes units
const formatYAxisTick = (value: any, unit?: string) => {
  let y = value
  //if value is number, convert to string
  if (typeof value === 'number') {
    y = formatNumber(y);
  } 

  if (!unit) return value;
  
  // Handle different unit types
  switch (unit) {
    case '$':
    case '€':
    case '£':
    case '¥':
      return `${unit}${y}`;
    case '%':
      return `${y}${unit}`;
    default:
      return `${y} ${unit}`;
  }
};

// Custom X-axis tick formatter that includes units
const formatXAxisTick = (value: any, unit?: string) => {
  if (!unit) return value;
  
  // For time units, don't add the unit to each tick (it's usually obvious)
  if (['days', 'months', 'years', 'hours', 'minutes', 'seconds'].includes(unit.toLowerCase())) {
    return value;
  }
  
  return `${value} ${unit}`;
};

// Custom tooltip formatter that includes units
const formatTooltipValue = (value: any, unit?: string) => {
  let y = value
  //if value is number, convert to string
  if (typeof value === 'number') {
    y = formatNumber(y);
  } 

  if (!unit) return value;
  
  // Handle different unit types
  switch (unit) {
    case '$':
    case '€':
    case '£':
    case '¥':
      return `${unit}${y}`;
    case '%':
      return `${y}${unit}`;
    default:
      return `${y} ${unit}`;
  }
};

export const LineChartComponent: React.FC<LineChartComponentProps> = React.memo(({
  data,
  config,
  visualizationKeys,
  colors,
  id,
}) => {
  const { yAxisUnit, xAxisUnit } = data;

  return (
    <div className="p-4" id={id}>
      {/* Display unit information if available */}
      {/* {(yAxisUnit || xAxisUnit) && (
        <div className="mb-2 text-sm text-muted-foreground">
          {yAxisUnit && (
            <span className="mr-4">
              Y-axis: {yAxisUnit}
            </span>
          )}
          {xAxisUnit && (
            <span>
              X-axis: {xAxisUnit}
            </span>
          )}
        </div>
      )} */}
      
      <ChartContainer
        config={config}
        className="max-h-[calc(100vh-10rem)] min-h-[300px] w-full"
      >
        <LineChart accessibilityLayer data={data.data}  margin={{top: 12}}>
          <CartesianGrid vertical={false} />
          <XAxis 
            dataKey={data.xAxis} 
            tickLine={false} 
            tickMargin={10}
            axisLine={false}
            tickFormatter={(value) => formatXAxisTick(value, xAxisUnit)}
          />
          <YAxis 
            tickLine={false} 
            tickMargin={10} 
            axisLine={false}
            tickFormatter={(value) => formatYAxisTick(value, yAxisUnit)}
          />
          <ChartTooltip
            content={<ChartTooltipContent 
              valueFormatter={(value) => formatTooltipValue(value, yAxisUnit)}
            />} 
          />
          {/* <ChartTooltip
            content={<ChartTooltipContent />} 
          /> */}
          <ChartLegend content={<ChartLegendContent />} />
          {visualizationKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors[index]}
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ChartContainer>
    </div>
  );
});